/**
 * `/waste/reports` — the site's subscription to its emailed PDFs (POS2-1137 CP1).
 *
 * **Under Waste, and open to every org user since POS2-1249.** Pinned decision 1 put this at a
 * top-level `/reports`; that was reversed at Artem's request, so the nav item lives in the Waste
 * group and nowhere else — the surface is not doubled — and `/reports` is the redirect, which keeps
 * any link written while the route was top-level working.
 *
 * It was platform-admin-only until 2026-09-07, behind a hook written to be deleted rather than
 * flipped. That deletion is the whole of POS2-1249's code change. The other
 * half of the pair, `REPORT_SCHEDULER_ENABLED`, decides whether anything ever *sends* and has been
 * on in production since POS2-1251's rehearsal.
 *
 * **The four cards come from a static catalogue and nothing is seeded per site.** `GET /reports`
 * returns nought to four rows and a card with no row is the never-configured state, so "every site
 * gets four from day one" is true by construction — no onboarding write, no backfill, and a
 * brand-new site and an unreachable endpoint render the same honest page.
 *
 * **No open rate, read rate or delivery telemetry appears here**, and the page does not ask the
 * API for any. That is an acceptance criterion of the ticket, asserted at the source level.
 */
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "@/i18n/use-translation";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import { useReportSite } from "@/hooks/use-report-site";
import {
  liveRecipients,
  useReportRuns,
  useReportSchedules,
  type ReportRun,
  type ReportSchedule,
} from "@/hooks/use-reports";
import { useToggleSchedule } from "@/hooks/use-report-toggle";
import { showApiError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { REPORT_CATALOGUE, type ReportKey } from "@/lib/report-catalogue";
import { ReportCard } from "@/components/reports/report-card";
import { RecentlySent } from "@/components/reports/recently-sent";
import { NewReportDialog } from "@/components/reports/new-report-dialog";
import { Button } from "@/components/ui/button";

/** One frozen empty array rather than a fresh `[]` per render — see `runs` below. */
const EMPTY: never[] = [];

// POS2-1249 deleted the platform-admin gate that used to wrap this page. The wrapper existed so a
// customer's visit made no request at all, since an early return inside the page would still have
// run every query above it — and there is no longer a visit that should not.
export default function ReportsPage() {
  const { locale, t } = useTranslation();
  const { siteId, siteName, timezone, isResolving } = useReportSite();
  const [requesting, setRequesting] = useState(false);
  const toggle = useToggleSchedule(siteId);
  // `isPlatformAdmin ||` mirrors the API exactly: `require_permission` returns early for a platform
  // admin before it looks at the code at all, so one without `report:configure` in their claim can
  // still write. Without this clause the editor would be hidden from somebody the server would
  // have let through — the shape `transaction-card.tsx` and `dashboard-kpis.tsx` already use.
  const { permissions, isPlatformAdmin } = useClaims();
  const canConfigure = isPlatformAdmin || hasPermission(permissions, "report:configure");

  const schedules = useReportSchedules(siteId).data ?? EMPTY;
  // A stable empty array, so `lastRuns` does not recompute on every render while nothing has run.
  const runs = useReportRuns(siteId).data ?? EMPTY;

  // Newest first from the endpoint, so the first run of a type is that type's last send.
  const lastRuns = useMemo(() => {
    const byType = new Map<ReportKey, ReportRun>();
    for (const run of runs) if (!byType.has(run.report_type)) byType.set(run.report_type, run);
    return byType;
  }, [runs]);

  const scheduled = schedules.filter((s) => !s.is_paused).length;

  /**
   * A toggle the card is allowed to flip, or null when the click belongs to the editor instead.
   *
   * Two cases go to the editor: a report nobody has set up, because it can never go live unseen,
   * and one paused because everyone was removed, because resuming it would send to nobody. Both
   * are decisions with a screen behind them rather than a switch that refuses.
   */
  function toggleFor(schedule: ReportSchedule | null) {
    if (!schedule) return null;
    // POS2-1313: pausing a report is a write, and Staff is a read-only role. `PUT /reports/{type}`
    // now refuses it — this is what stops the card offering a control that would 403. A third
    // early return rather than a new branch in the card, because "no toggle" is a shape the card
    // already has and already explains: it renders a switch that links to the preview instead of a
    // disabled one, since a control that will not move says nothing about why.
    if (!canConfigure) return null;
    if (schedule.is_paused && liveRecipients(schedule).length === 0) return null;
    return (paused: boolean) =>
      toggle.mutate(
        { schedule, paused },
        {
          onSuccess: (saved) =>
            // Resume confirms when the next one goes, quoting the server's cursor rather than
            // guessing; pause says "nothing new", because a send already in flight completes on
            // the old settings and promising otherwise would be a lie the worker cannot keep.
            toast.success(
              paused
                ? t("reports.card.pausedToast")
                : saved.next_run_at
                  ? t("reports.card.resumedToast", {
                      when: formatDateTime(locale, saved.next_run_at, {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: timezone,
                      }),
                    })
                  : t("reports.card.resumedToastNoDate"),
            ),
          onError: showApiError,
        },
      );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {/* Dashboard and Explore both name the selected site here; a viewer with several
              sites could not otherwise tell which one's reports they were configuring
              (POS2-1279). Long names truncate with the full name on hover. */}
          <h1 className="flex min-w-0 items-baseline gap-1.5 font-display text-xl font-extrabold tracking-tight text-gray-900">
            <span className="shrink-0">{t("reports.page.title")}</span>
            {siteName && (
              <>
                <span className="shrink-0 text-gray-300">·</span>
                <span className="max-w-[16rem] truncate" title={siteName}>
                  {siteName}
                </span>
              </>
            )}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("reports.page.subtitle", { count: scheduled, total: REPORT_CATALOGUE.length })}
          </p>
        </div>
        {siteId && (
          <Button variant="outline" onClick={() => setRequesting(true)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("reports.request.button")}
          </Button>
        )}
      </header>

      {!siteId && !isResolving ? (
        <p className="text-sm text-gray-500">{t("reports.page.noSite")}</p>
      ) : (
        <>
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-sm font-semibold text-gray-900">
                {t("reports.page.sectionTitle")}
              </h2>
              <p className="text-xs text-gray-400">{t("reports.page.sectionHint")}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {REPORT_CATALOGUE.map((entry) => (
                <ReportCard
                  key={entry.key}
                  entry={entry}
                  schedule={schedules.find((s) => s.report_type === entry.key) ?? null}
                  lastRun={lastRuns.get(entry.key) ?? null}
                  timezone={timezone}
                  busy={toggle.isPending}
                  onToggle={toggleFor(schedules.find((s) => s.report_type === entry.key) ?? null)}
                  canConfigure={canConfigure}
                />
              ))}
            </div>
          </section>

          <RecentlySent runs={runs} timezone={timezone} />
        </>
      )}

      {requesting && siteId && (
        <NewReportDialog siteId={siteId} onClose={() => setRequesting(false)} />
      )}
    </div>
  );
}
